import { dashboardSectionBandPadding } from "@/lib/dashboardToolbarUi";
import { CONTENT_MAX_WIDTH } from "@/lib/useDeviceType";
import type { CourseThumbnailItem } from "@/types";
import React, { useCallback, useRef } from "react";
import { FlatList, TouchableOpacity, View } from "react-native";
import { SizableText, XStack, YStack } from "tamagui";
import { SectionTitle } from "@/components/ui/Typography";
import { CourseThumbnailCard } from "./CourseThumbnailCard";
import { CarouselArrowButton } from "./horizontalCarousel/CarouselArrowButton";
import { useCarouselLeftPadding } from "./horizontalCarousel/useCarouselLeftPadding";
import { useHorizontalCarouselScroll } from "./horizontalCarousel/useHorizontalCarouselScroll";

type CourseThumbnailsSectionProps = {
  title: string;
  data: CourseThumbnailItem[];
  onPressCourse: (slug: string) => void;
  onSeeAll?: () => void;
  seeAllText?: string;
};

export const CourseThumbnailsSection = React.memo(function CourseThumbnailsSection({
  title,
  data,
  onPressCourse,
  onSeeAll,
  seeAllText = "See All",
}: CourseThumbnailsSectionProps) {
  const onPressCourseRef = useRef(onPressCourse);
  onPressCourseRef.current = onPressCourse;
  const onSeeAllRef = useRef(onSeeAll);
  onSeeAllRef.current = onSeeAll;
  const leftPadding = useCarouselLeftPadding();

  const renderItem = useCallback(
    ({ item }: { item: CourseThumbnailItem }) => (
      <CourseThumbnailCard
        id={item.id}
        slug={item.slug}
        thumbnail={item.thumbnail}
        progressPercentage={item.progressPercentage}
        onPress={() => onPressCourseRef.current(item.slug)}
      />
    ),
    [],
  );

  const {
    listRef,
    isAtStart,
    isAtEnd,
    handleLayout,
    handleContentSizeChange,
    handleScroll,
    scrollToDirection,
    stableFlatListRenderItem,
    buttonSize,
    iconSize,
    itemGap,
    contentPaddingVertical,
  } = useHorizontalCarouselScroll(data.length, renderItem, {
    enableWebDragScroll: false,
  });

  if (data.length === 0) return null;

  const shouldShowSeeAll = !!onSeeAll;

  return (
    <YStack
      {...dashboardSectionBandPadding}
      gap="$1"
      bg="white"
      position="relative"
    >
      <YStack w="100%" maw={CONTENT_MAX_WIDTH} mx="auto" px={16}>
        <XStack ai="center" gap={12} minWidth={0}>
          <YStack f={1} minWidth={0}>
            <SectionTitle
              color="$color"
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {title}
            </SectionTitle>
          </YStack>
          {shouldShowSeeAll && (
            <TouchableOpacity
              onPress={() => onSeeAllRef.current?.()}
              accessibilityRole="link"
              accessibilityLabel={`${seeAllText} ${title}`}
              style={{ flexShrink: 0, cursor: "pointer" } as object}
            >
              <SizableText
                size="$4"
                $gtSm={{ size: "$5" }}
                fontWeight="600"
                color="#987451"
              >
                {seeAllText}
              </SizableText>
            </TouchableOpacity>
          )}
        </XStack>
      </YStack>

      <XStack ai="center" position="relative">
        {!isAtStart && (
          <CarouselArrowButton
            direction="left"
            buttonSize={buttonSize}
            iconSize={iconSize}
            onPress={() => scrollToDirection("left")}
          />
        )}

        <FlatList
          ref={listRef}
          onLayout={handleLayout}
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled
          data={data}
          renderItem={stableFlatListRenderItem}
          keyExtractor={(item) => String(item.id)}
          contentContainerStyle={{
            paddingLeft: leftPadding,
            paddingRight: 16,
            paddingVertical: contentPaddingVertical,
          }}
          onContentSizeChange={handleContentSizeChange}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          ItemSeparatorComponent={() => <View style={{ width: itemGap }} />}
        />

        {!isAtEnd && (
          <CarouselArrowButton
            direction="right"
            buttonSize={buttonSize}
            iconSize={iconSize}
            onPress={() => scrollToDirection("right")}
          />
        )}
      </XStack>
    </YStack>
  );
});
